<script setup lang="ts">
    import { useForm } from '@inertiajs/vue3';
    import ProcedureForm from './ProcedureForm.vue';
    import DialogForm from '@/components/DialogForm.vue';
    import QueryKey from '@/enums/QueryKey';
    import type { Procedure } from '@/types';
    import { Button } from '@/components/ui/button';
    import { DialogFooter, DialogClose } from '@/components/ui/dialog';
    import { useI18n } from 'vue-i18n';
    import Permission from '@/enums/Permission';
    import { usePermissions } from '@/composables/usePermissions';
    import { usePage } from '@inertiajs/vue3';
    import { computed, ref } from 'vue';
    import type { SharedData } from '@/types';
    import DeleteProcedure from './DeleteProcedure.vue';
    import ContinueButton from '@/components/ContinueButton.vue';

    const props = defineProps<{
        procedure: Procedure;
    }>();

    const { t } = useI18n();
    const { hasPermission } = usePermissions();

    const page = usePage<SharedData>();
    const isClient = computed(() => page.props.auth.user.is_client);

    const form = useForm({
        procedure_id: props.procedure.procedure_id,
        company_id: props.procedure.company_id,
        procedure_task: props.procedure.procedure_task,
        procedure_responsible: props.procedure.procedure_responsible_ids,
        procedure_deadline: props.procedure.procedure_deadline,
    });

    const dialogFormRef = ref<InstanceType<typeof DialogForm>>();

    const handleDelete = () => {
        dialogFormRef.value?.invalidateQueryAndClose();
    };
</script>

<template>
    <DialogForm
        ref="dialogFormRef"
        type="update"
        method="post"
        :form="form"
        :route="route('v2.dashboard.procedures.update')"
        :query-key="[QueryKey.DASHBOARD_PROCEDURES, props.procedure.company_id]"
        title="dashboards.procedures_edit"
    >
        <template
            v-if="isClient"
            #trigger
        >
            <ContinueButton>
                {{ $t('forms.open') }}
            </ContinueButton>
        </template>

        <ProcedureForm :form="form" />

        <template #footer>
            <DialogFooter class="gap-2 border-t sm:justify-center">
                <DeleteProcedure
                    v-if="hasPermission(Permission.DASHBOARD_ADMIN_DELETE)"
                    :procedure="procedure"
                    @deleted="handleDelete"
                />

                <DialogClose as-child>
                    <Button
                        variant="outline"
                        size="sm"
                        @click="form.resetAndClearErrors()"
                    >
                        {{ $t('forms.cancel') }}
                    </Button>
                </DialogClose>

                <Button
                    v-if="hasPermission(Permission.DASHBOARD_ADMIN_CREATE)"
                    :disabled="form.processing"
                    :loading="form.processing"
                    size="sm"
                    variant="default"
                >
                    {{ t('button-text.save') }}
                </Button>
            </DialogFooter>
        </template>
    </DialogForm>
</template>
